import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { FormProvider } from '../fluidComponents/fluidForm/FormContext';
import { DocFluidForm } from '../fluidComponents/fluidForm/DocFluidForm.component';
import { FluidForm } from '../../shared';
import { registerFormInstance } from '../fluidComponents/fluidForm/FormInstance.service';
import { Container } from 'semantic-ui-react';
import { FieldComponent } from '../fluidComponents/fluidForm/fields/FieldComponent';
import { SimpleFormProvider } from '../fluidComponents/fluidForm/SimpleFormLoader';
import { ExampleAuthWrapper } from "./ExampleAuthWrapper";
import * as SemanticUiReact from 'semantic-ui-react';
import { EmbeddedStyles } from '../fluidComponents/fluidForm/EmbeddedStyles';
// Expose components to window so the injected script can access them
window.FluidComponents = {
    React,
    createRoot,
    FormProvider,
    DocFluidForm,
    FluidForm,
    registerFormInstance,
    Container,
    FieldComponent,
    SimpleFormProvider,
    SemanticUiReact,
    EmbeddedStyles
};
// Also expose React and ReactDOM globally for Babel-transformed JSX
window.React = React;
export const PureJsForm = () => {
    return (React.createElement(ExampleAuthWrapper, { formConfig: {
            key: "pure-js-form",
            type: "form",
            fields: [
                { key: "Full Name", label: "Full Name", type: "fullName", required: true },
                { key: "Email", label: "Email", type: "email", required: true },
                { key: "Address", label: "Address", type: "address" }
            ]
        } },
        React.createElement(PureJsFormInner, null)));
};
const jsxCode2 = `
   const { 
      React, createRoot, FormProvider, DocFluidForm, FluidForm, 
      registerFormInstance, Container, SimpleFormProvider, 
      FieldComponent, SemanticUiReact, EmbeddedStyles
    } = window.FluidComponents;

  function FieldBlock({ label, fieldPath }) {
    return (
      <SemanticUiReact.Form.Field className="mb-4">
        <div className="mt-1">
          <FieldComponent fieldPath={fieldPath} />
        </div>
      </SemanticUiReact.Form.Field>
    );
  }

  /**
   * ---------------------------------------------
   * Form Setup
   * ---------------------------------------------
   */

  const fluidForm = new FluidForm(
    {
      key: "pure-js-form",
      type: "form",
      fields: [
        { key: "Full Name", label: "Full Name", type: "fullName", required: true },
        { key: "Email", label: "Email", type: "email", required: true },
        { key: "Address", label: "Address", type: "address" }
      ]
    },
    {},
    {
      styleConfig: {
        style: "classic",
        formInputModesPosition: "top",
        inputModes: ["email", "address"]
      }
    }
  );

  const instanceId = registerFormInstance({ form: fluidForm });

  /**
   * ---------------------------------------------
   * Render
   * ---------------------------------------------
   */

  const container = document.getElementById("pure-js-container");
  const root = createRoot(container);

  root.render(
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <SemanticUiReact.Segment className="w-full max-w-2xl shadow-lg">
        <SemanticUiReact.Header as="h2" textAlign="center" className="mb-6">
          <SemanticUiReact.Icon name="wpforms" />
          <SemanticUiReact.Header.Content>
            Example Form
          </SemanticUiReact.Header.Content>
        </SemanticUiReact.Header>

        <SimpleFormProvider
          formInstance={instanceId}
          render={(formProps, onSubmit, validationResults) => (
            <EmbeddedStyles>
            <SemanticUiReact.Form
              className="space-y-4"
            >
              {/* Explicit fields (no mapping) */}
              <FieldBlock
                label="Full Name"
                fieldPath="Full Name"
              />

              <FieldBlock
                label="Email"
                fieldPath="Email"
              />

              <FieldBlock
                label="Address"
                fieldPath="Address"
              />

              {/* Actions */}
              <div className="flex justify-end pt-4">
                <SemanticUiReact.Button
                  primary
                  size="large"
                  onClick={() =>
                    onSubmit(fluidForm.getFormSubmission())
                  }
                >
                  Submit
                </SemanticUiReact.Button>
              </div>
            </SemanticUiReact.Form>
            </EmbeddedStyles>
          )}
        />
      </SemanticUiReact.Segment>
    </div>
  );
`;
export const PureJsFormInner = () => {
    const codeRef = useRef(null);
    const [babelLoaded, setBabelLoaded] = useState(false);
    // JSX code - much cleaner than React.createElement!
    //language=jsx
    const jsxCode = `
    const { 
      React, createRoot, FormProvider, DocFluidForm, FluidForm, 
      registerFormInstance, Container, SimpleFormProvider, 
      FieldComponent, SemanticUiReact, EmbeddedStyles
    } = window.FluidComponents;
    
    // Create the form service
    const fluidForm = new FluidForm({
      key: "example-form",
      type: "form",
      fields: [
        { key: "Full Name", label: "Full Name", type: "fullName" },
        { key: "Email", label: "Email", type: "email" },
        { key: "Address", label: "Address", type: "address" }
      ]
    }, {}, {
      styleConfig: {
        style: "classic",
        formInputModesPosition: "top",
        inputModes: ["email", "address"]
      }
    });

    // Register the form instance
    const instanceId = registerFormInstance({ form: fluidForm });

    const container = document.getElementById('pure-js-container');
    const root = createRoot(container);
    
    root.render(
      <SimpleFormProvider
        formInstance={instanceId}
        render={(formProps, onSubmit, validationResults) => (
          <>
            <FieldComponent fieldPath="Full Name" />
            <FieldComponent fieldPath="Email" />
            <FieldComponent fieldPath="Address" />
            <SemanticUiReact.Button onClick={() => onSubmit(fluidForm.getFormSubmission())}>
              Submit
            </SemanticUiReact.Button>
          </>
        )}
      />
    );
  `;
    useEffect(() => {
        // Load Babel standalone if not already loaded
        if (!window.Babel) {
            const babelScript = document.createElement('script');
            babelScript.src = 'https://unpkg.com/@babel/standalone@7.23.5/babel.min.js';
            babelScript.onload = () => setBabelLoaded(true);
            document.head.appendChild(babelScript);
        }
        else {
            setBabelLoaded(true);
        }
    }, []);
    useEffect(() => {
        if (!babelLoaded || !codeRef.current)
            return;
        try {
            // Transform JSX to JavaScript using Babel
            const transformedCode = window.Babel.transform(jsxCode2, {
                presets: ['react'],
            }).code;
            // Execute the transformed code
            const script = document.createElement('script');
            script.innerHTML = transformedCode;
            codeRef.current.appendChild(script);
        }
        catch (error) {
            console.error('Babel transform error:', error);
        }
    }, [babelLoaded]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { id: "pure-js-container" }),
        React.createElement("div", { ref: codeRef })));
};
